import { useAuthStore } from '@/auth';

/**
 * Known clinician feature flags. Source of truth: v1 audit + DOMAIN_MODEL.md §10.
 * Treat unknown flags as `false` (fail closed).
 */
export type ClinicianFlag =
  | 'clinician_show_timeline'
  | 'clinician_meal_planner_enabled'
  | 'clinician_autopilot_enabled'
  | 'clinician_allow_meal_edit'
  | 'clinician_can_delete_meals'
  | 'clinician_can_generate_reports'
  | 'clinician_can_view_clinic_invite_code'
  | 'clinic_enable_clinician_advisor_chat'
  | 'clinician_patient_allow_mindful_eating_survey_toggle';

export const useClinicianFlag = (flag: ClinicianFlag): boolean => {
  const flags = useAuthStore((s) => s.clinician?.flags) as Record<string, unknown> | undefined;
  return flags?.[flag] === true;
};

/**
 * Reads multiple flags at once. Useful when a component branches on several.
 * Returns `false` for any flag not explicitly set to `true`.
 */
export const useClinicianFlags = <T extends ClinicianFlag>(...names: T[]): Record<T, boolean> => {
  const flags = useAuthStore((s) => s.clinician?.flags) as Record<string, unknown> | undefined;
  const result = {} as Record<T, boolean>;
  for (const name of names) {
    result[name] = flags?.[name] === true;
  }
  return result;
};
